import { FileType, QueryFactory, TemplateDto } from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import {
  defaultDocumentName,
  getFileNameWithFormat,
  isDocumentNew,
} from '@lmt/unicorn-shared/app/components/documents/richedit/richedit-helper';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { getTabId } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { EventArgs } from 'devexpress-richedit';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';
import {
  DXR,
  RichEdit,
  SavingEventArgs,
} from '../../../common/devexpress/RichEdit/dx-richedit';

import { openTemplateMenuId } from './OpenTemplatesRichEditMenuItem';
import { unfocusRichEditor } from './unfocusRichEditor';
import { useEventCallback } from '@mui/material';
import { DocumentFormatApi } from 'devexpress-richedit/lib/client/model-api/formats/enum';

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  template: TemplateDto | null;
  patientId: number;
  blockId: number | undefined;
  documentId: string | undefined;
  groupId: string | undefined;
  onNewDocumentId?: (newId: string) => void;
};

/*
  Component that adds and handles "Save" action for documents (not templates!)
 */
export const SaveDocumentRichEditButton: React.FC<Props> = React.memo(
  function SaveDocumentRichEditButton(props) {
    const {
      richEdit,
      template,
      patientId,
      blockId,
      documentId,
      groupId,
      onNewDocumentId,
    } = props;
    const dispatch = useUniDispatch();
    const queryClient = useQueryClient();
    const i18n = useScopedTranslation('documents');
    const location = useLocation();
    const [lastSavedDocumentId, setLastSavedDocumentId] = useState(documentId);

    const tabId = getTabId(location);
    const claims = useClaims();
    const { data: versionHistory } =
      QueryFactory.FileQuery.useGetVersionHistoryQuery(groupId!, {
        enabled: !!groupId,
      });
    useEffect(() => {
      dispatch(
        NavigationActions.setHandler({
          tabId: tabId!,
          deactivateHandler() {
            if (
              !richEdit.current.hasUnsavedChanges ||
              isDocumentNew(richEdit.current)
            )
              return;
            uploadBlob(richEdit.current).finally(() => {
              setTabHasUnsavedChanges(false);
            });
          },
        }),
      );
    }, []);
    useEffect(() => {
      setLastSavedDocumentId(documentId);
    }, [documentId]);

    const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
      if (tabId) {
        dispatch(
          NavigationActions.tabHasUnsavedChangesChanged({
            id: tabId,
            hasUnsavedChanges: hasChanges,
          }),
        );
      }
    });

    const uploadBlob = useEventCallback(async (s: RichEdit | undefined) => {
      if (!s?.hasUnsavedChanges) {
        return;
      }
      const fileName = s.fileName;
      s?.exportToBlob(async (data: Blob) => {
        try {
          const file = {
            fileName: getFileNameWithFormat(
              fileName,
              DXR.DocumentFormat.OpenXml,
            ),
            data: data,
          };
          if (lastSavedDocumentId) {
            const fileInfo = await ClientFactory.FileClient.updateFile(
              lastSavedDocumentId,
              file,
            );

            onNewDocumentId?.(fileInfo.id);
            setLastSavedDocumentId(fileInfo.id);
            setTabHasUnsavedChanges(false);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
                patientId: patientId,
              }),
            });

            queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
                groupId!,
              ),
            });
            if (blockId) {
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
                  dataBlockId: blockId,
                }),
              });
            }
          } else {
            const fileInfo = await ClientFactory.FileClient.create(
              FileType.DocumentDraft,
              `Template: ${fileName}`,
              blockId,
              patientId,
              null,
              template?.id,
              null,
              file,
            );

            onNewDocumentId?.(fileInfo.id);
            setLastSavedDocumentId(fileInfo.id);
            setTabHasUnsavedChanges(false);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
                patientId: patientId,
              }),
            });
            queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
                groupId!,
              ),
            });
            if (blockId) {
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
                  dataBlockId: blockId,
                }),
              });
            }
            try {
              s.updateRibbon?.((ribbon) => {
                if (!ribbon) return;
                const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
                newFilePanel.removeItem(openTemplateMenuId);
                if (!claims.permissions.createOrEditDocuments) {
                  newFilePanel.removeItem(DXR.FileTabItemId.ExportDocument);
                }
              });
            } catch (e) {
              // if RichEdit was unloaded (e.g due to language change, see #51954, we might get errors here,
              // which doesn't make sense to show )
            }
          }
          try {
            s.hasUnsavedChanges = false;
          } catch (e) {
            // if RichEdit was unloaded (e.g due to language change, see #51954, we might get errors here,
            // which doesn't make sense to show )
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      }, DocumentFormatApi.OpenXml);
    });

    const checkIfDocumentHasUnsavedChanges = useEventCallback(
      (s: RichEdit | undefined) => {
        if (!s || (isDocumentNew(s) && !lastSavedDocumentId)) {
          setTabHasUnsavedChanges(true);
          return;
        }
        if (s?.hasUnsavedChanges) {
          setTabHasUnsavedChanges(true);
        } else {
          setTabHasUnsavedChanges(false);
        }
      },
    );
    const modals = useModal();
    const saveDocument = useEventCallback(
      async (s: RichEdit | undefined, e: SavingEventArgs | undefined) => {
        if (!s || !e) {
          return;
        }

        if (isDocumentNew(s) && e.fileName === defaultDocumentName) {
          await unfocusRichEditor();
          let newName: string | null = '';

          newName = await modals.showPrompt({
            defaultValue: newName,
            text: '',
            fieldName: i18n.t('save_new_file_as_name_prompt.text'),
            title: i18n.t('save_new_file_as_name_prompt.title'),
            isMandatory: true,
            maxLength: 200,
            validate: (value: string) => {
              if (/[\/\\]/.test(value)) {
                return i18n.t(
                  'query_builder.errors.file_name_invalid_characters',
                );
              }

              return true;
            },
          });

          if (newName === null) {
            return;
          }

          s.fileName = newName;
          e.fileName = newName;
        }

        try {
          s.loadingPanel.show();
          await uploadBlob(s);
          toast(i18n.t('file_saved_toast'), {
            type: 'success',
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          s.loadingPanel?.hide();
        }
      },
    );

    const onDocumentChanged = useEventCallback(
      async (s: RichEdit | undefined, e: EventArgs | undefined) => {
        if (!s) {
          return;
        }
        if (!isDocumentNew(s)) await uploadBlob(s);
      },
    );

    const onDocumentChangedDebounced = useDebouncedCallback(
      onDocumentChanged,
      20 * 1000,
      {
        trailing: true,
        maxWait: 60 * 1000,
      },
    );

    const documentLoadedHandler = useCallback(() => {
      // fix #54055: "Save" button is still active after saving a patient document
      richEdit.current.hasUnsavedChanges = false;
    }, []);

    useEffect(() => {
      richEdit.current.events.saving.addHandler(saveDocument);
      richEdit.current.events.documentChanged.addHandler(
        onDocumentChangedDebounced,
      );
      richEdit.current.events.documentChanged.addHandler(
        checkIfDocumentHasUnsavedChanges,
      );
      richEdit.current.events.documentLoaded.addHandler(documentLoadedHandler);
      return () => {
        richEdit.current.events.saving.removeHandler(saveDocument);
        richEdit.current.events.documentChanged.removeHandler(
          onDocumentChangedDebounced,
        );
        richEdit.current.events.documentChanged.removeHandler(
          checkIfDocumentHasUnsavedChanges,
        );
        richEdit.current.events.documentLoaded.removeHandler(
          documentLoadedHandler,
        );
      };
    }, [
      saveDocument,
      onDocumentChangedDebounced,
      checkIfDocumentHasUnsavedChanges,
    ]);

    return null;
  },
);
